// ============================================================================
// AuthContext — Phase 2.0.1
// ============================================================================
// Thin Provider that owns the auth-related useState hooks previously declared
// at the top of App(). All effects (auth listener, visibility refresh,
// access-gate redirect), helpers (loadUser, canAccessProgram),
// and derived values (role, isAdmin, canEditAll, etc.) remain in App.jsx —
// they read state via useAuth() and call the setters exposed here.
//
// The shape of the state is unchanged from the pre-migration version:
//   authState   : null (loading) | false (signed out) | { user, role, profile, name }
//   pwRecovery  : boolean — true when URL hash indicates recovery/invite link
//   sessionEpoch/sessionRunId : lease-fenced authentication generation tokens.
//     They guard a released route claim but never imply that a global data
//     payload exists; each released surface owns its own resource readiness.
//   profileStatus : unresolved/resolving/resolved/failed/signed-out. A failed
//     profile resolves to App's fail-closed inactive Header recovery rather
//     than a data-loading retry screen.
//   saveStatus  : '' | 'saving' | 'saved' | 'error'
//   The shared admin profile directory deliberately does NOT live here. Its
//   on-demand list has a separate scoped owner in src/shared/userDirectoryData.
// ============================================================================
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import {sb} from '../lib/supabase.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {appQueryClient} from '../app/data/queryClient.js';

// The signed-in auth user, DERIVED from the client this app already creates
// rather than named through a second import of the Supabase library. That is
// not stylistic: tests/static/supabase_client_owner_static.test.js asserts the
// whole repository contains exactly one occurrence of that package specifier
// and it counts TEXT, so even a fully-erased `import type` of it would break
// the single-owner boundary. src/lib/supabaseTypes.ts records the same
// reasoning and uses the same `typeof sb` derivation.
type SupabaseUser = NonNullable<Awaited<ReturnType<SupabaseClient['auth']['getUser']>>['data']['user']>;

// The three states `authState` can hold, and no more than this module can
// actually justify.
//
//   * `user` is always the Supabase session user — App.loadUser is only ever
//     called with `session.user` / `data.user`, so the derivation above is the
//     real type, not a guess.
//   * `role` is a plain string: App resolves it from the profile row (or
//     'inactive' on the fail-closed path) and withRolePreview below overwrites
//     it with a member of ROLE_PREVIEW_ROLES. Deliberately NOT narrowed to that
//     union — nothing enforces the union at runtime and pretending otherwise
//     would be a claim about data this module never validates.
//   * `profile` is a `profiles` table row. Its columns live in SQL, this
//     repository has no generated database types, and the only thing this file
//     ever establishes about it is `typeof … === 'object'`. `unknown` is what is
//     known; a hand-written column list would be invented. A later wave that
//     actually consults the schema can narrow it.
//   * the two preview markers are optional members rather than a separate
//     variant. withRolePreview returns an identity carrying them, and TypeScript
//     reduces that union back to AuthIdentity because it is a subtype — which
//     would have made two fields this file demonstrably produces invisible to
//     every reader of `authState`. Declaring them describes the code; it invents
//     nothing.
export type AuthIdentity = {
  user: SupabaseUser;
  role: string;
  profile?: unknown;
  name?: string;
  rolePreviewActive?: boolean;
  realRole?: string;
};
export type AuthState = AuthIdentity | false | null;

// Profile resolution and session generations are diagnostic/auth-runtime
// metadata. They say WHICH authenticated epoch owns the route claim so an old
// async completion can never be mistaken for the current subject.
export const PROFILE_STATUS = Object.freeze({
  UNRESOLVED: 'unresolved',
  RESOLVING: 'resolving',
  RESOLVED: 'resolved',
  FAILED: 'failed',
  SIGNED_OUT: 'signed-out',
} as const);

export type ProfileStatus = (typeof PROFILE_STATUS)[keyof typeof PROFILE_STATUS];

export type SessionLifecycle = {
  /** Runs before the manual Supabase sign-out starts; must invalidate leases. */
  beforeSignOut?: () => void;
  /** Runs after the mandatory cache clear, when App may reset readiness. */
  afterSignOut?: () => void;
};

export const ROLE_PREVIEW_ROLES = ['admin', 'management', 'farm_team', 'equipment_tech', 'light', 'inactive'];

function withRolePreview(authState: AuthState, rolePreview: string) {
  // `authState === false` is tested BEFORE the truthiness check, which is the
  // only edit inside this function. `!authState` already removes `false` from
  // the union, so with the original order TypeScript sees the later `=== false`
  // as a comparison between two types that cannot overlap and reports it. The
  // two operands are pure and the `||` chain is order-independent, so the
  // runtime result is identical for every input.
  if (authState === false || !authState || !rolePreview || !ROLE_PREVIEW_ROLES.includes(rolePreview)) return authState;
  return {
    ...authState,
    role: rolePreview,
    // Diagnostic markers only: consumers can identify preview state without
    // touching Supabase auth, the JWT, or the stored profile row.
    rolePreviewActive: true,
    realRole: authState.role,
    profile:
      authState.profile && typeof authState.profile === 'object'
        ? {...authState.profile, role: rolePreview}
        : authState.profile,
  };
}

// All of this provider's STATE, in one hook, so the context value type is
// INFERRED from what the provider actually produces instead of being restated
// by hand. Twenty-six members — every setter's `Dispatch<SetStateAction<…>>`
// included — come from this for free.
//
// The signed-in identity state stays here. The full admin profile directory is
// not an auth prerequisite and has its own on-demand provider, so auth boot
// cannot accidentally become the owner of a whole-farm roster read.
function useAuthState() {
  const [realAuthState, setAuthState] = useState<AuthState>(null);
  const [rolePreview, setRolePreviewRaw] = useState('');
  const [pwRecovery, setPwRecovery] = useState(() => {
    if (typeof window === 'undefined') return false;
    const h = window.location.hash || '';
    const q = window.location.search || '';
    return (
      /[#&?]type=recovery\b/.test(h) ||
      /[?&]type=recovery\b/.test(q) ||
      /[#&?]type=invite\b/.test(h) ||
      /[?&]type=invite\b/.test(q)
    );
  });
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const [sessionRunId, setSessionRunId] = useState(0);
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>(PROFILE_STATUS.UNRESOLVED);
  const [saveStatus, setSaveStatus] = useState('');

  // Operand order flipped for the same reason as withRolePreview above, with
  // the same runtime result.
  const canUseRolePreview = !!(realAuthState !== false && realAuthState && realAuthState.role === 'admin');
  useEffect(() => {
    if (!canUseRolePreview && rolePreview) setRolePreviewRaw('');
  }, [canUseRolePreview, rolePreview]);

  const setRolePreview = useCallback((nextRole: string) => {
    setRolePreviewRaw(ROLE_PREVIEW_ROLES.includes(nextRole) ? nextRole : '');
  }, []);
  const clearRolePreview = useCallback(() => setRolePreviewRaw(''), []);
  const activeRolePreview = canUseRolePreview ? rolePreview : '';
  const authState = useMemo(
    () => (activeRolePreview ? withRolePreview(realAuthState, activeRolePreview) : realAuthState),
    [realAuthState, activeRolePreview],
  );

  return {
    authState,
    realAuthState,
    setAuthState,
    rolePreview: activeRolePreview,
    setRolePreview,
    clearRolePreview,
    rolePreviewRoles: ROLE_PREVIEW_ROLES,
    canUseRolePreview,
    pwRecovery,
    setPwRecovery,
    sessionEpoch,
    setSessionEpoch,
    sessionRunId,
    setSessionRunId,
    profileStatus,
    setProfileStatus,
    saveStatus,
    setSaveStatus,
  };
}

// The state above plus the one AuthProvider action. Its type is read straight
// off the one-expression body, which is the whole of what there is to know.
type AuthContextValue = ReturnType<typeof useAuthState> & {
  signOut: () => Promise<void>;
  registerSessionLifecycle: (lifecycle: SessionLifecycle) => () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({children}: {children?: ReactNode}) {
  const state = useAuthState();
  // AuthProvider owns the button's Supabase call and cache-clear ordering;
  // App owns the live coordinator. A ref gives the two owners a narrow,
  // synchronous lifecycle seam without moving the legacy 600-line loader into
  // this provider or asking Header to know about boot state.
  const sessionLifecycleRef = useRef<SessionLifecycle | null>(null);

  const registerSessionLifecycle = useCallback((lifecycle: SessionLifecycle) => {
    sessionLifecycleRef.current = lifecycle;
    return () => {
      if (sessionLifecycleRef.current === lifecycle) sessionLifecycleRef.current = null;
    };
  }, []);

  // B2.2c: moved out of App() so Header can sign out without being handed an
  // App closure. The provider still owns the Supabase call/cache ordering;
  // its registered lifecycle lets App invalidate its own boot lease first.
  const signOut = useCallback(async () => {
    try {
      sessionLifecycleRef.current?.beforeSignOut?.();
    } catch (error) {
      console.warn('pre-sign-out lifecycle cleanup failed:', error);
    }
    try {
      await sb.auth.signOut();
    } finally {
      // Query cache retains entries after their last observer unsubscribes (see
      // gcTime in src/app/data/queryClient.js). Farm devices are shared, so the
      // outgoing user's rows must not sit in memory for the next person — the
      // query KEYS carry the full subject scope, so nothing can be read under
      // the wrong key, but "unreadable" is not "gone".
      //
      // Ordered AFTER the Supabase sign-out on purpose: any in-flight query that
      // resolves during sign-out has already lost its session and cannot
      // repopulate the cache with privileged rows. Clearing first would leave
      // that window open.
      //
      // clear() rather than removeQueries(): it drops the whole cache including
      // any entry a surface added without going through the route catalog.
      // Nothing here is worth keeping across an identity change.
      //
      // In a `finally` so an UNEXPECTED throw from sb.auth.signOut() cannot skip
      // it. Ordinary failures resolve with {error} rather than throwing, so this
      // is the pathological case — but the whole point of the clear is that it
      // must not be skippable, and a sign-out that threw is exactly when the
      // outgoing user's rows are most likely to still be resident.
      appQueryClient.clear();
      try {
        // App resets auth/readiness only after cache residency is cleared.
        sessionLifecycleRef.current?.afterSignOut?.();
      } catch (error) {
        console.warn('post-sign-out lifecycle cleanup failed:', error);
      }
    }
  }, []);

  const value = {...state, signOut, registerSessionLifecycle};
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Asserts the provider, and that assertion is what gives all 84 call sites a
// non-null type. It is not a new failure mode: the hook used to return `null`
// outside its provider and every consumer destructures the result immediately,
// so the app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
